import cronstrue from "cronstrue";
import { Cron } from "croner";

export interface CronAnalysis {
  ok: boolean;
  error?: string;
  description?: string;
  warnings: string[];
  nextRuns: Date[];
}

export function analyzeCron(expr: string, timezone: string, count = 5): CronAnalysis {
  const text = expr.trim().replace(/\s+/g, " ");
  const warnings: string[] = [];
  if (!text) return { ok: false, error: "", warnings, nextRuns: [] };

  const fields = text.split(" ");
  if (!text.startsWith("@") && (fields.length < 5 || fields.length > 7)) {
    return {
      ok: false,
      error: "A cron expression has 5 fields: minute hour day-of-month month day-of-week (or use @daily, @hourly, …).",
      warnings,
      nextRuns: [],
    };
  }

  let job: Cron;
  try {
    job = new Cron(text, { timezone });
  } catch (e) {
    const msg = e instanceof Error ? e.message.replace(/^CronPattern:\s*/, "") : "Invalid cron expression";
    return { ok: false, error: msg, warnings, nextRuns: [] };
  }

  let description = "";
  try {
    description = cronstrue.toString(text.startsWith("@") ? text : text, { use24HourTimeFormat: true });
  } catch {
    description = "";
  }

  if (fields.length === 6 || fields.length === 7) {
    warnings.push(
      "This has 6–7 fields (seconds and/or year). Standard Linux/Unix cron uses 5; Quartz and AWS EventBridge use their own 6–7 field formats with different rules."
    );
  }
  if (fields.length === 5) {
    const [, , dom, , dow] = fields;
    if (dom !== "*" && dom !== "?" && dow !== "*" && dow !== "?") {
      warnings.push(
        "Day-of-month and day-of-week are both set. Standard cron runs when EITHER matches (OR), not when both match."
      );
    }
  }

  const nextRuns: Date[] = [];
  try {
    for (const d of job.nextRuns(count)) if (d) nextRuns.push(d);
  } catch {
    /* ignore */
  }
  if (nextRuns.length === 0) {
    warnings.push("This schedule never runs (for example, February 31st).");
  }

  return { ok: true, description, warnings, nextRuns };
}

export function formatRun(d: Date, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
}
